import React, { Fragment, memo, useCallback, useState } from 'react'
import tw from 'twin.macro'
import { Form, Formik } from 'formik'
import { Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { ConfirmActionModal } from 'src/components/shared/ConfirmActionModal'
import { DataRow } from 'src/components/shared/DataRow'
import { FormikRadioGroup } from 'src/components/shared/form/formik/FormikRadioGroup'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { AdminExecuteRefund } from 'src/constants/actionTypes'
import { US_STATE_OPTIONS } from 'src/constants/oatfi-business/states'
import { RefundDestinationType, RefundPaymentRail } from 'src/constants/payments-ops/enums'
import {
  refundPaymentRailLabels,
  refundReferenceTypeLabels,
} from 'src/constants/payments-ops/labels'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import type { ExecuteRefundResult, FetchRefundsPayload, Refund } from 'src/interfaces/refund'
import { getRefundExecuting } from 'src/selectors/refund'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'
import { usePartnerNameMap } from 'src/utils/hooks/paymentsOps/usePartnerNameMap'
import { resolvePartnerName } from 'src/utils/paymentsOps/partnerNames'
import {
  buildExecuteRefundBody,
  buildRefundRecapDescription,
  formatRefundRecipientAddress,
  isRefundAddressRequired,
} from 'src/utils/paymentsOps/refundExecute'
import type { ExecuteRefundFormValues } from 'src/utils/paymentsOps/refundExecute.types'
import { REFUND_ADDRESS_MAX_LENGTHS, executeRefundValidator } from './ExecuteRefundModal.validator'

interface Props {
  refund: Refund | null
  refetch: FetchRefundsPayload
  onClose(): void
}

const FORM_ID = 'execute-refund-form'

const INITIAL_VALUES: ExecuteRefundFormValues = {
  destinationType: RefundDestinationType.GigsafeAccount,
  paymentRail: RefundPaymentRail.Ach,
  recipientName: '',
  routingNumber: '',
  accountNumber: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
}

const DESTINATION_OPTIONS = [
  {
    value: RefundDestinationType.GigsafeAccount,
    label: "GigSafe account — refund back to GigSafe's settlement account",
  },
  {
    value: RefundDestinationType.CustomerAccount,
    label: 'Customer account — send to a customer-provided bank account',
  },
]

const PAYMENT_RAIL_OPTIONS = [
  {
    value: RefundPaymentRail.Ach,
    label: refundPaymentRailLabels[RefundPaymentRail.Ach],
  },
  {
    value: RefundPaymentRail.Rtp,
    label: `${refundPaymentRailLabels[RefundPaymentRail.Rtp]} — instant`,
  },
]

const ExecuteRefundModalComponent = ({ refund, refetch, onClose }: Props) => {
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const nameMap = usePartnerNameMap()
  const isExecuting = useSelector(getRefundExecuting)

  // The recap values are kept after the recap closes so its description keeps naming the same
  // destination while the modal fades out; only `isRecapOpen` says whether it is showing.
  const [recapValues, setRecapValues] = useState<ExecuteRefundFormValues | null>(null)
  const [isRecapOpen, setIsRecapOpen] = useState<boolean>(false)

  const handleReview = useCallback((values: ExecuteRefundFormValues) => {
    setRecapValues(values)
    setIsRecapOpen(true)
  }, [])

  // Money is moving: no path (Cancel, backdrop, Escape) may dismiss either modal until the write
  // settles, otherwise the outcome would land on a closed modal and the operator would not see it.
  const handleClose = useCallback(() => {
    if (isExecuting) {
      return
    }

    onClose()
  }, [isExecuting, onClose])

  const handleCloseRecap = useCallback(() => {
    if (isExecuting) {
      return
    }

    setIsRecapOpen(false)
  }, [isExecuting])

  const handleConfirm = useCallback(async () => {
    if (!refund || !recapValues) {
      return
    }

    const result = await asyncDispatchWithErrorToast<ExecuteRefundResult>(
      AdminExecuteRefund.request({
        refundId: refund.id,
        body: buildExecuteRefundBody(recapValues),
        refetch,
      }),
    )

    // Dismissal is blocked while the write is in flight, so both modals are still open here.
    // Closing the recap also keeps the next opened refund from inheriting it.
    setIsRecapOpen(false)

    if (!result) {
      return
    }

    const warnings = result.warnings ?? []

    snackbar?.enqueueSnackbar(
      warnings.length > 0 ? `Refund sent. ${warnings.join(' ')}` : 'Refund sent — now in progress.',
      { variant: warnings.length > 0 ? 'info' : 'success' },
    )
    onClose()
  }, [asyncDispatchWithErrorToast, onClose, recapValues, refetch, refund, snackbar])

  if (!refund) {
    return null
  }

  const amount = formatCentsAsUsd(refund.amount)
  const partnerName = resolvePartnerName(nameMap, refund.partnerId)
  const recapDescription = recapValues ? buildRefundRecapDescription(recapValues, amount) : ''
  const recapAddress = recapValues ? formatRefundRecipientAddress(recapValues) : null
  const recapDetails = recapValues ? (
    <div css={tw`flex flex-col gap-2 rounded-lg bg-shades-50 p-4`}>
      <DataRow
        title="Payment rail"
        text={refundPaymentRailLabels[recapValues.paymentRail] ?? recapValues.paymentRail}
      />
      {recapAddress ? <DataRow title="Address" text={recapAddress} direction="column" /> : null}
    </div>
  ) : null

  return (
    <Fragment>
      <Formik<ExecuteRefundFormValues>
        initialValues={INITIAL_VALUES}
        validationSchema={executeRefundValidator}
        onSubmit={handleReview}
      >
        {({ values }) => {
          return (
            <CommonModal
              isOpen={!isRecapOpen}
              title="Execute refund"
              className={tw`max-w-[520px]`}
              onClose={handleClose}
              buttons={
                <Fragment>
                  <Button size="small" color="grey" disabled={isExecuting} onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    color="dark"
                    type="submit"
                    form={FORM_ID}
                    data-testid="execute-refund-review"
                  >
                    Review…
                  </Button>
                </Fragment>
              }
            >
              <Form id={FORM_ID} css={tw`flex flex-col gap-4`}>
                <div css={tw`flex flex-col gap-2 rounded-lg bg-shades-50 p-4`}>
                  <DataRow title="Amount" text={amount} />
                  <DataRow title="Partner" text={partnerName} />
                  <DataRow
                    title="Reference"
                    text={`${
                      refundReferenceTypeLabels[refund.referenceType] ?? refund.referenceType
                    } · ${refund.referenceId}`}
                    direction="column"
                  />
                  {refund.failureReason ? (
                    <DataRow title="Prior failure" text={refund.failureReason} direction="column" />
                  ) : null}
                </div>
                <FormikRadioGroup<ExecuteRefundFormValues>
                  name="destinationType"
                  label="Destination"
                  options={DESTINATION_OPTIONS}
                  dataTestId="execute-refund-destination"
                />
                <FormikRadioGroup<ExecuteRefundFormValues>
                  name="paymentRail"
                  label="Payment rail"
                  options={PAYMENT_RAIL_OPTIONS}
                  dataTestId="execute-refund-payment-rail"
                />
                {values.destinationType === RefundDestinationType.CustomerAccount ? (
                  <Fragment>
                    <Typography variant="body2" css={tw`text-xs text-shades-500`}>
                      Routing must be 9 digits with a valid checksum; account 6–17 digits.
                    </Typography>
                    <FormikTextField<ExecuteRefundFormValues>
                      name="recipientName"
                      label="Recipient name *"
                      placeholder="Recipient name"
                    />
                    <FormikTextField<ExecuteRefundFormValues>
                      name="routingNumber"
                      label="Routing number *"
                      placeholder="9 digits"
                      inputProps={{ maxLength: 9, inputMode: 'numeric' }}
                    />
                    <FormikTextField<ExecuteRefundFormValues>
                      name="accountNumber"
                      label="Account number *"
                      placeholder="6–17 digits"
                      inputProps={{ maxLength: 17, inputMode: 'numeric' }}
                    />
                  </Fragment>
                ) : null}
                {isRefundAddressRequired(values.destinationType, values.paymentRail) ? (
                  <Fragment>
                    <Typography variant="body2" css={tw`text-xs text-shades-500`}>
                      RTP to a customer account needs the recipient's address.
                    </Typography>
                    <FormikTextField<ExecuteRefundFormValues>
                      name="addressLine1"
                      label="Address line 1 *"
                      placeholder="Street address"
                      inputProps={{ maxLength: REFUND_ADDRESS_MAX_LENGTHS.addressLine1 }}
                    />
                    <FormikTextField<ExecuteRefundFormValues>
                      name="addressLine2"
                      label="Address line 2"
                      placeholder="Apartment, suite, unit (optional)"
                      inputProps={{ maxLength: REFUND_ADDRESS_MAX_LENGTHS.addressLine2 }}
                    />
                    <FormikTextField<ExecuteRefundFormValues>
                      name="city"
                      label="City *"
                      placeholder="City"
                      inputProps={{ maxLength: REFUND_ADDRESS_MAX_LENGTHS.city }}
                    />
                    <div css={tw`grid grid-cols-2 gap-4`}>
                      <FormikSelect<ExecuteRefundFormValues>
                        name="state"
                        label="State *"
                        placeholder="Select a state"
                        options={US_STATE_OPTIONS}
                      />
                      <FormikTextField<ExecuteRefundFormValues>
                        name="zipCode"
                        label="ZIP *"
                        placeholder="12345 or 12345-6789"
                        inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                      />
                    </div>
                  </Fragment>
                ) : null}
              </Form>
            </CommonModal>
          )
        }}
      </Formik>
      <ConfirmActionModal
        isOpen={isRecapOpen}
        title="Send refund?"
        description={recapDescription}
        details={recapDetails}
        confirmLabel={`Send ${amount}`}
        isDestructive
        isSubmitting={isExecuting}
        onConfirm={handleConfirm}
        onClose={handleCloseRecap}
        confirmDataTestId="execute-refund-confirm"
      />
    </Fragment>
  )
}

export const ExecuteRefundModal = memo(ExecuteRefundModalComponent)
